import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ClipboardList, QrCode } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import type { XkStation } from '@/lib/api/types';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { ExportXlsxButton } from '@/components/ui/ExportXlsxButton';
import { usePagination } from '@/lib/hooks/usePagination';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';

export function StationListPage() {
  const t = useT();
  const brandId = useSelectedBrandId();
  useFavoritable(t('stations.title'));
  const { data, isLoading } = useQuery({
    queryKey: brandId
      ? queryKeys.stations.byBrand(brandId)
      : queryKeys.stations.all,
    queryFn: () =>
      api.list<XkStation>(
        '/xk-station',
        brandId ? { filter: { companyId: brandId } } : undefined,
      ),
  });
  const allItems = data?.items;
  const pager = usePagination(allItems, 12);

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            {t('stations.title')}
            <HelpHint section="stations-list" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('stations.list.subtitleLong')}
          </p>
        </div>
        <ExportXlsxButton
          filename="stations"
          sheetName="Stations"
          disabled={!allItems || allItems.length === 0}
          rows={() => (allItems ?? []).map(stationToExportRow)}
        />
      </header>

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : !allItems || allItems.length === 0 ? (
        <EmptyState
          icon={<ClipboardList className="h-6 w-6" />}
          title={t('stations.empty.title')}
          description={t('stations.list.emptyDesc')}
          action={
            <Link to="/projects">
              <Button>{t('stations.list.goToProjects')}</Button>
            </Link>
          }
        />
      ) : (
        <Card data-tour="stations-list">
          <CardHeader>
            <CardTitle>{t('stations.list.allStations')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
                <tr>
                  <th className="py-2 text-left">{t('stations.column.name')}</th>
                  <th className="py-2 text-left">{t('stations.column.event')}</th>
                  <th className="py-2 text-left">{t('stations.list.column.survey')}</th>
                  <th className="py-2 text-right">{t('stations.list.column.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--color-border-subtle)]">
                    <td className="py-3 font-semibold">{s.name}</td>
                    <td className="py-3 text-[var(--color-fg-muted)]">#{s.eventId}</td>
                    <td className="py-3 text-[var(--color-fg-muted)]">
                      {s.surveysId ? `#${s.surveysId}` : '—'}
                    </td>
                    <td className="py-3 text-right">
                      <div className="inline-flex gap-2">
                        <Link to={`/stations/${s.id}`}>
                          <Button size="sm" variant="outline">
                            {t('stations.list.allocate')}
                          </Button>
                        </Link>
                        <Link to={`/stations/${s.id}/qr`}>
                          <Button size="sm" leftIcon={<QrCode className="h-4 w-4" />}>
                            {t('stations.list.qrShort')}
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination {...pager} noun={t('stations.title').toLowerCase()} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function stationToExportRow(s: XkStation): Record<string, unknown> {
  return {
    ID: s.id,
    Name: s.name,
    'Event ID': s.eventId,
    'Journey ID': s.surveysId ?? '',
    Status: s.status ?? '',
    'Created at': s.createdAt ?? '',
  };
}
